import {
  Box,
  Container,
  Divider,
  Heading,
  Stack,
  Text,
} from '@chakra-ui/react';
import clsx from 'clsx';
import { GetServerSidePropsResult, NextPage } from 'next';
import { Fragment } from 'react';
import { fetchAPI } from '../../lib/api';
import {
  HallendienstEntry,
  HallendienstPlan,
  TrainingsZeiten,
} from '../../types';
import {
  Patterns,
  formatDate,
  timeToDate,
  toISO,
  nextWeekday,
  normalice,
} from '../../utils/dateUtils';
import { formatISO, isAfter, parse } from 'date-fns';
import { None, Option, Some } from '@sniptt/monads';
import { BlockManager } from '../../container/BlockManager';

interface TrainingProps {
  hallendienst: HallendienstPlan;
  trainingsZeiten: TrainingsZeiten;
}

const Row = function (props: { entry: HallendienstEntry; next?: boolean }) {
  return (
    <Box
      className={clsx(
        'border-default-800 bg-default-400  rounded-default grid grid-cols-8 border-2 p-4',
        { 'border-4': props.next }
      )}
    >
      <Heading size={'md'} className="col-span-2 mr-4 inline">
        {formatDate(props.entry.attributes.date.date, Patterns.TIMELESS)}
      </Heading>
      <span className="col-span-2">
        {formatDate(
          timeToDate(props.entry.attributes.date.from),
          Patterns.TIME_ONLY_NO_UNIT
        )}
        -
        {formatDate(
          timeToDate(props.entry.attributes.date.to),
          Patterns.TIME_ONLY
        )}
      </span>
      <span className="col-span-2 col-start-7 text-center">
        {props.entry.attributes.verantwortlicher
          ? props.entry.attributes.verantwortlicher
          : '---'}
      </span>
      {props.entry.attributes.kommentar && (
        <div className="col-span-full">{props.entry.attributes.kommentar}</div>
      )}
    </Box>
  );
};

const TrainingPage: NextPage<TrainingProps> = function (props) {
  const trainings: Option<HallendienstPlan> = Some(props.hallendienst).andThen(
    (t) => (t.length > 0 ? Some(t) : None)
  );

  const normalTrainingsToHallenplan = Some(
    props.trainingsZeiten.attributes.zeiten
  ).map((zeiten) => {
    return zeiten.map((z, i) => {
      const date = nextWeekday(z.date.day, new Date());

      const e: HallendienstEntry = {
        id: i,
        attributes: {
          createdAt: props.trainingsZeiten.attributes.createdAt,
          createdBy: props.trainingsZeiten.attributes.createdBy,
          updatedAt: props.trainingsZeiten.attributes.createdAt,
          updatedBy: props.trainingsZeiten.attributes.updatedBy,
          publishedAt: props.trainingsZeiten.attributes.publishedAt,
          date: {
            date: toISO(date),
            from: z.date.from,
            to: z.date.to,
          },
          kommentar: z.title,
          verantwortlicher: z.verantwortlicher
            ? z.verantwortlicher
            : 'Trainer|Vorstand',
          seo: undefined,
        },
      };

      return e;
    });
  });

  const allTrainings = trainings.map((training) => {
    const normal = normalTrainingsToHallenplan.unwrapOr([]);

    return [...training, ...normal].sort(
      (a, b) =>
        normalice(a.attributes.date.date).valueOf() -
        normalice(b.attributes.date.date).valueOf()
    );
  });

  return (
    <Fragment>
      <Container maxWidth={'container.lg'} className="mb-8">
        <Heading as="h3" size="lg" className="mb-4">
          Trainings Zeiten
        </Heading>
        {Some(props.trainingsZeiten.attributes.blocks).match({
          some(blocks) {
            return (
              <Box>
                <BlockManager blocks={blocks} />
              </Box>
            );
          },
          none: <Fragment />,
        })}
        <Stack>
          {props.trainingsZeiten.attributes.zeiten
            .filter((zeit) => zeit.anzeigen)
            .map((z) => (
              <Box className="bg-default-600 dark:text-default-700 px-2 py-2 text-white dark:bg-white">
                {z.title} - {z.date.day}{' '}
                {formatDate(
                  parse(z.date.from, 'HH:mm:ss', new Date()),
                  Patterns.TIME_ONLY_NO_UNIT
                )}{' '}
                -{' '}
                {formatDate(
                  parse(z.date.to, 'HH:mm:ss', new Date()),
                  Patterns.TIME_ONLY
                )}
              </Box>
            ))}
        </Stack>
      </Container>
      <Container maxWidth={'container.lg'}>
        <Heading as="h2" size="lg" className="mb-4">
          Hallendienst am Wochenende
        </Heading>
        <Text className="mb-8">
          Wenn sich niemand für den Schlüsseldienst meldet, fällt das Training
          aus. Meldet euch unter{' '}
          <b className="inline-block">vorstand(at)svl-bogensport.de</b>
        </Text>
        {allTrainings.match({
          some(val) {
            return (
              <Stack>
                <div>
                  <Heading size={'lg'}>Nächstes training:</Heading>
                  <Row entry={val[0]} next />
                </div>
                <Divider className="mb-8" />
                {val.slice(1).map((e) => (
                  <Row entry={e} />
                ))}
              </Stack>
            );
          },
          none: (
            <Box>
              <Text>Kein Training gefunden</Text>
            </Box>
          ),
        })}
      </Container>
    </Fragment>
  );
};

type StaticPropsResult = GetServerSidePropsResult<TrainingProps>;

export async function getServerSideProps(): Promise<StaticPropsResult> {
  const entries = fetchAPI<HallendienstPlan>('/schluesseldiensts', {
    'sort[0]': 'date.date',
    'sort[1]': 'date.from',
    populate: '*',
    filters: {
      date: {
        date: {
          $gt: formatISO(new Date()),
        },
      },
    },
  });

  const trainings = fetchAPI<TrainingsZeiten>('/trainingszeiten', {
    populate: {
      zeiten: {
        populate: '*',
      },
      blocks: {
        populate: '*',
      },
    },
  });

  const [hallendienst, trainingszeiten] = await Promise.all([
    entries,
    trainings,
  ]);

  return {
    props: {
      hallendienst: hallendienst.data,
      trainingsZeiten: trainingszeiten.data,
    },
  };
}

export default TrainingPage;
